<template>
  <Container class="!max-w-[1362px]">
    <div v-if="data?.subtitle || data?.title" class="mb-12 flex flex-col items-center lg:mb-20">
      <SubTitle v-if="data?.subtitle" class="mb-3 text-blue">
        {{ data?.subtitle }}
      </SubTitle>
      <Title v-if="data?.title">
        {{ data?.title }}
      </Title>
    </div>
    <Collapse
      v-for="(card, index) in data?.cards"
      :id="slugify(card.title || card.id || '')"
      :key="card.id"
      :open="data?.firstExpanded && index === 0"
    >
      <template #header>{{ card?.title }}</template>
      <template #content>
        <div
          class="flex flex-col"
          :class="[
            reverse(index) ? 'justify-between lg:flex-row-reverse' : 'justify-between lg:flex-row',
          ]"
        >
          <Image
            v-if="card?.cta?.image?.url"
            :url="card?.cta?.image?.url"
            :alt="card?.cta?.image?.alt"
            :class="[
              'aspect-[3/2] w-full shrink-0 overflow-hidden rounded-b-2xl lg:aspect-[3/2.5] lg:basis-[45%] xl:basis-[38%]',
              reverse(index)
                ? 'lg:rounded-bl-2xl lg:rounded-br-none lg:rounded-tl-2xl'
                : 'lg:rounded-bl-none lg:rounded-br-2xl lg:rounded-tr-2xl',
            ]"
            width="800"
            height="400"
            sizes="xs:100vw sm:800"
            fit="cover"
          />

          <div
            v-if="card?.cta?.subtitle && card?.cta?.title && card?.cta?.copy"
            class="mb-3.5 shrink self-center px-4 pb-0 pt-6 lg:mb-0 lg:px-28 lg:pb-10 lg:pt-10"
          >
            <div class="max-w-2xl">
              <SubTitle v-if="card?.cta?.subtitle" class="text-blue">
                {{ card?.cta?.subtitle }}
              </SubTitle>
              <Title v-if="card?.cta?.title" :custom-style="useDarkMode ? 'text-white' : ''">
                {{ card?.cta?.title }}
              </Title>
              <div
                v-if="card?.cta?.copy"
                :class="[
                  'mt-3.5 font-sans text-base leading-snug lg:text-lg lg:leading-normal',
                  useDarkMode ? 'text-white' : 'text-black',
                ]"
                v-html="card?.cta?.copy"
              ></div>

              <LinkSecondary
                v-if="card?.cta?.link && !card?.copy"
                :title="card?.cta?.link?.title"
                :link="card?.cta?.link.url"
                :external="card?.cta?.link?.type === 'ext'"
                class="mt-3.5"
                type="blue"
              />
            </div>
          </div>
        </div>
        <div class="max-w-4xl px-4 lg:pl-28">
          <article
            v-if="card?.copy"
            :class="
              twMerge(
                'custom-list prose prose-a:text-blue prose-a:no-underline hover:prose-a:text-blue-dark lg:prose-lg mb-4 mt-4 lg:mb-16 lg:mt-16',
                useDarkMode ? 'prose-p:text-white prose-li:text-white prose-strong:text-white' : '',
              )
            "
            v-html="card?.copy"
          ></article>

          <LinkSecondary
            v-if="card?.cta?.link && card?.copy"
            :title="card?.cta?.link?.title"
            :link="card?.cta?.link.url"
            :external="card?.cta?.link?.type === 'ext'"
            type="blue"
            class="mb-8 mt-3.5 lg:mb-16"
          />
        </div>
        <NuxtLink
          v-if="card.linkVisible && (card.title || card.id)"
          external
          :to="removeCmsDomain(normalizeURL(`#${slugify(card.title || card.id || '')}`))"
          :aria-label="card.title || card.id"
          class="m-4 inline-block cursor-pointer self-end text-blue hover:text-blue-darker lg:mb-6 lg:mr-8 lg:mt-6"
        >
          <PermaLink aria-hidden="true" />
        </NuxtLink>
      </template>
    </Collapse>
  </Container>
</template>

<script setup lang="ts">
// Sibling: packages/astro-ui/src/components/DynamicBlocks/FaqBlock.astro
import slugify from '@sindresorhus/slugify'
import { normalizeURL } from 'ufo'
import { removeCmsDomain } from '../../utils/urlFilter'
import Collapse from '../atoms/Collapse.vue'
import Container from '../atoms/Container.vue'
import PermaLink from '../atoms/Icons/PermaLink.vue'
import Image from '../atoms/Image.vue'
import LinkSecondary from '../atoms/LinkSecondary.vue'
import SubTitle from '../atoms/SubTitle.vue'
import Title from '../atoms/Title.vue'

import type { ICardData, ICtaData } from '../../types'
import { computed, inject, ref, type Ref } from 'vue'
import { twMerge } from 'tailwind-merge'
export interface ICardFaqData extends ICardData {
  linkVisible?: boolean
  cta?: ICtaData
}

export interface IFaqBlock {
  id: string
  title?: string
  subtitle?: string
  firstExpanded?: boolean
  cards?: ICardFaqData[]
}

interface Props {
  data: IFaqBlock
}

defineProps<Props>()

const settings = inject<Ref<string[]>>('settings', ref([]))
const useDarkMode = computed(() => settings.value?.includes('dark_theme') ?? false)

const reverse = (index: number) => index % 2 === 0
</script>
